/**
 * Roof SKELETON — the stable, queryable contract over the face solver (ROOF round,
 * 2026-08; docs/MODELER.md "the roof solver must expose a queryable SKELETON").
 * Classified edges (ridge / hip / valley / eave / rake), face planes with slope
 * direction, and PLATE lines — promoted from solver internals to a derived output.
 * Future auto-derived structure (ridge beams, rafters at spacing, exposed tails,
 * eave assemblies — PARKED per the re-sequencing note) consumes this exactly like
 * tags consume openings: derived at generation, never hand-placed, driven by
 * roof-TYPE params. Nothing here is speculative — the solver already computed all of
 * it; this module only names it. NOTHING consumes it yet besides the UI's joint
 * grips; treat the shapes as a contract, not an implementation detail.
 *
 * Everything is SERIALIZABLE (no closures): a face's plane is (origin, zOriginFt,
 * risePerRun, uphill) with z(p) = zOriginFt + risePerRun · ((p − origin) · uphill).
 * All elevations are on the BEARING plane (the underside, where structure seats) —
 * the finished top surface sits t·√(1+m²) plumb above it (see generate3d).
 */
import type { Pt2, ResolvedElement } from '@aj/bim-kernel'
import { solveRoofForElement, collinearOverlap, type RoofFace } from './solve'
import { clipFacesToJoined, resolveRoofJoin } from './join'
import { withOverframe } from './overframe'
import { clipFacesToCuts, roofCutRings } from './verticalCuts'

export type Pt3 = [number, number, number]

/** 'step' (2026-08-12, the defines-slope tear round): a TEAR boundary — two
 *  faces meeting along one plan line at DIFFERENT z (the telescoping-gable
 *  vertical step). 'seam' (r3): a COPLANAR interior boundary — a flying-rake
 *  piece abutting its parent face across a tear; one continuous sheet, no fold.
 *  Neither is ever dressed: trim and framing consume explicit kinds only. */
export type RoofEdgeKind = 'ridge' | 'hip' | 'valley' | 'eave' | 'rake' | 'step' | 'seam'

export interface RoofSkeletonFace {
  /** index into the INPUT boundary edge list (the bearing edge this face springs
   *  from) — indexes `boundary.nodes` / `solved.specs` directly for any winding
   *  since the 2026-08-19 remap in solveRoofFaces */
  edgeIndex: number
  /** plan region of the face */
  ring: Pt2[]
  /** slope as rise per 12 of run */
  slopeRise: number
  /** CORNER LAPS: a steeper eave's piece tucked under the shallow overhang (cornerLap.ts) */
  tucked?: boolean
  /** unit plan direction of DESCENT (toward the eave); [0,0] for a flat face */
  downhill: Pt2
  /** bearing plane: z(p) = zOriginFt + risePerRun · ((p − origin) · uphill) */
  plane: { origin: Pt2; zOriginFt: number; risePerRun: number; uphill: Pt2 }
}

export interface RoofSkeletonEdge {
  kind: RoofEdgeKind
  a: Pt3
  b: Pt3
  /** indices into `faces` of every face this edge bounds (1 = perimeter, 2 = joint) */
  faces: number[]
  /** CORNER LAPS: trim joins this run with the collinear run it continues */
  joinRun?: boolean
}

export interface RoofPlateLine {
  /** the boundary (bearing) edge this plate line is */
  edgeIndex: number
  a: Pt3
  b: Pt3
}

export interface RoofSkeleton {
  /** the eave polygon (bearing line offset outward per-edge by its overhang) */
  eaveRing: Pt2[]
  faces: RoofSkeletonFace[]
  /** every unique face-boundary edge, classified; z on the bearing plane */
  edges: RoofSkeletonEdge[]
  /**
   * Where faces cross wall tops: each live (non-gable, non-clipped-away) face's
   * bearing edge at the plate elevation. v1 emits the FULL bearing edge — clipping
   * to the face's plan region on non-convex footprints is a recorded refinement.
   */
  plateLines: RoofPlateLine[]
  zPlateFt: number
}

// ROOF-PLAN MISCLASSIFICATION incident (2026-08-20, Matt: "the hips on here are
// actually ridges"; "one hip text is located away from the actual hip,
// suggesting a portion of a valley is a hip"): LEVEL_TOL and SIDE_TOL were
// calibrated for idealized, noise-free floating point, but any edge that's
// passed through a roof-to-roof JOIN's clip (`clipFacesToJoined`, Clipper2's
// own 1e-4 ft integer grid) or two independently-solved bearing planes meeting
// at a line carries REAL, EXPECTED numerical drift — the exact "drawing
// tolerance" class of noise this codebase already tolerates elsewhere (the
// TEAR check three lines below, `1e-6 + len·2e-4`, r5's own "a ridge resolved
// on drawing-tolerance off-parallel fronts carries z-mismatch up to
// drift·length — still a ridge"; generate3d.ts's 2e-3 ft NEAR-coplanar test).
// Measured on a real live model: a genuine ridge's two independently-computed
// endpoints disagreed by up to 4.6e-4 ft (0.006") — 20x the OLD level
// tolerance at that length — while every genuine hip in the same roof showed
// ≥0.12 ft of real rise. LEVEL_TOL now reuses the tear check's own proven
// formula outright (same drift source, same fix); SIDE_TOL — a probe at a
// FIXED lateral offset (SIDE_EPS), not edge-length-scaled, so the tear
// formula doesn't apply — moves from 1e-9 to 1e-3 ft, comfortably above the
// same noise floor and far below any real valley signal (a valley's
// least-sloped real face in practice still clears inches, not thousandths).
const LEVEL_TOL_OF = (len: number) => 1e-6 + len * 2e-4
const SIDE_EPS = 0.05
const SIDE_TOL = 1e-3

/**
 * Classify a shared face-boundary edge by probing each face's OWN plane on its own
 * side of the edge (the side its ring winding puts the face interior on): at a
 * VALLEY each face's plane is the HIGHER of the two on its own side (the surface
 * rises transverse to the edge in both directions); at a crest each face's plane is
 * the lower (ridge when level, hip when sloped). Pre-straight-skeleton this probed
 * the lower envelope of the two planes — which is exactly wrong at a true valley
 * (locally the surface is the planes' UPPER envelope), so 'valley' could never fire.
 */
function classifyShared(
  faces: RoofFace[],
  occ: { fi: number; fwd: boolean }[],
  a: Pt2,
  b: Pt2,
  za: number,
  zb: number,
): RoofEdgeKind {
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const len = Math.hypot(dx, dy)
  const level = Math.abs(za - zb) <= LEVEL_TOL_OF(len)
  if (len < 1e-9 || occ.length !== 2) return level ? 'ridge' : 'hip'
  const mid: Pt2 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
  // the face interior is LEFT of the edge as its own CCW ring traverses it
  const ownSide = (fwd: boolean): Pt2 => {
    const s = fwd ? 1 : -1
    return [mid[0] + (-dy / len) * SIDE_EPS * s, mid[1] + (dx / len) * SIDE_EPS * s]
  }
  const f1 = faces[occ[0].fi], f2 = faces[occ[1].fi]
  // TEAR (2026-08-12): the two faces disagree on z ALONG the edge itself — a
  // vertical step (telescoping gable), not a fold. Classify before the
  // valley/ridge probes, which all assume the surfaces meet at the edge.
  // Tolerance is DRIFT-SCALED (r5 — the missing-ridge-board report): a ridge
  // resolved on drawing-tolerance off-parallel fronts carries z-mismatch up to
  // drift·length between its two planes at the solved line — still a ridge,
  // not a step (a real telescoping step is inches to feet).
  if (Math.abs(f1.zAt(mid) - f2.zAt(mid)) > 1e-6 + len * 2e-4) return 'step'
  // SEAM (r3): same z AND same plane — a flying rake abutting its parent
  // across the tear. One continuous sheet: no ridge/hip member, no trim.
  if (Math.abs(f1.m - f2.m) <= 1e-9 &&
      f1.uphill[0] * f2.uphill[0] + f1.uphill[1] * f2.uphill[1] >= 1 - 1e-9) return 'seam'
  const q1 = ownSide(occ[0].fwd), q2 = ownSide(occ[1].fwd)
  if (f1.zAt(q1) > f2.zAt(q1) + SIDE_TOL && f2.zAt(q2) > f1.zAt(q2) + SIDE_TOL) return 'valley'
  return level ? 'ridge' : 'hip'
}

const keyPt = (p: Pt2) => `${Math.round(p[0] * 1e5)},${Math.round(p[1] * 1e5)}`
const edgeKeyOf = (a: Pt2, b: Pt2) => {
  const ka = keyPt(a), kb = keyPt(b)
  return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`
}

/** The skeleton for a resolved roof element — null when it has no solvable faces. */
export function roofSkeletonFor(el: ResolvedElement): RoofSkeleton | null {
  const solved = solveRoofForElement(el)
  return roofSkeletonOf(solved)
}

/**
 * The skeleton over the POST-JOIN clipped solve (bim-plan/14 slice D — closing the
 * recorded contract gap: `roofSkeletonOf` existed for callers already holding a
 * clipped solve, but nothing packaged the solve→join→clip→classify chain for a
 * consumer that starts from the element, so joined roofs' derived annotation would
 * ride the unclipped lines "in mid-air"). Same three calls generate3d's roofSolid
 * makes. Falls back to the plain unclipped skeleton when `byId` is absent or the
 * roof isn't joined.
 */
export function roofSkeletonClipped(
  el: ResolvedElement,
  byId?: Map<string, ResolvedElement>,
): RoofSkeleton | null {
  let solved = solveRoofForElement(el)
  if (!solved) return null
  const join = resolveRoofJoin(el, solved, byId)
  if (join?.authority && join.targetSolved) {
    solved = clipFacesToJoined(el, solved, join.authority, join.targetSolved)
    if (!solved.faces.length) return null
  }
  return roofSkeletonOf(solved)
}

/**
 * The solve the roof is actually BUILT from (generate3d#roofSolid's order): overframe cut
 * loops → join clip → vertical cuts. roofSkeletonClipped stops after the join; this one is
 * for consumers that must match the built roof exactly (the section's assembly-depth
 * insulation). Null when nothing is left.
 */
export function roofSolveAsBuilt(
  el: ResolvedElement,
  byId?: Map<string, ResolvedElement>,
): ReturnType<typeof solveRoofForElement> {
  const elB = withOverframe(el, byId)
  let solved = solveRoofForElement(elB)
  if (!solved || !solved.faces.length) return null
  const join = resolveRoofJoin(elB, solved, byId)
  if (join?.authority && join.targetSolved) {
    solved = clipFacesToJoined(elB, solved, join.authority, join.targetSolved)
    if (!solved.faces.length) return null
  }
  const cuts = roofCutRings(elB)
  if (cuts.length) {
    solved = { ...solved, faces: clipFacesToCuts(solved.faces, cuts) }
    if (!solved.faces.length) return null
  }
  return solved
}

/**
 * The census/classification over an ALREADY-SOLVED roof (barrel-tile r4): callers
 * holding a post-join CLIPPED solve (roofSolid passes tiles.ts the faces after
 * clipFacesToJoined) classify THOSE — roofSkeletonFor re-solves unclipped, and
 * deriving caps/setbacks from it put cap runs in mid-air and cut phantom slots
 * where the unclipped lines crossed joined roofs (Matt's "more flack in awkward
 * cut areas" + the floating tile fragments).
 */
export function roofSkeletonOf(solved: ReturnType<typeof solveRoofForElement>): RoofSkeleton | null {
  if (!solved || !solved.faces.length) return null
  const { faces, eaveRing, zPlateFt } = solved

  // unique-edge census with adjacency — each occurrence keeps its DIRECTION so the
  // classifier knows which side of the edge each face's interior is on
  const census = new Map<string, { a: Pt2; b: Pt2; faces: number[]; occ: { fi: number; fwd: boolean }[] }>()
  faces.forEach((f, fi) => {
    // CORNER LAPS: a tucked piece lies under the shallow sheet — kept out of the shared-edge
    // census (its edges are classified on their own below)
    if (f.tucked) return
    for (let i = 0; i < f.ring.length; i++) {
      const a = f.ring[i], b = f.ring[(i + 1) % f.ring.length]
      const k = edgeKeyOf(a, b)
      const rec = census.get(k)
      if (rec) {
        rec.faces.push(fi)
        rec.occ.push({ fi, fwd: keyPt(a) === keyPt(rec.a) })
      } else {
        census.set(k, { a: [a[0], a[1]], b: [b[0], b[1]], faces: [fi], occ: [{ fi, fwd: true }] })
      }
    }
  })

  const edges: RoofSkeletonEdge[] = []
  // CORNER LAPS: the tuck cleanup's edge decisions (the shallow eave carried on to where it
  // meets the steep roof), and the tucked pieces' own edges — level = the steep eave carried
  // on (fascia + gutter follow, joined to the run it continues), the rest undressed
  const ovKey = new Map<string, { kind: RoofEdgeKind; edgeIndex: number }>()
  for (const o of solved.edgeOverrides ?? []) ovKey.set(edgeKeyOf(o.a, o.b), { kind: o.kind, edgeIndex: o.edgeIndex })
  faces.forEach((f, fi) => {
    if (!f.tucked) return
    for (let i = 0; i < f.ring.length; i++) {
      const a = f.ring[i], b = f.ring[(i + 1) % f.ring.length]
      const za = f.zAt(a), zb = f.zAt(b)
      const lvl = Math.abs(za - zb) <= LEVEL_TOL_OF(Math.hypot(b[0] - a[0], b[1] - a[1]))
      edges.push({ kind: lvl ? 'eave' : 'step', a: [a[0], a[1], za], b: [b[0], b[1], zb], faces: [fi], ...(lvl ? { joinRun: true } : {}) })
    }
  })
  for (const rec of census.values()) {
    const ov = ovKey.get(edgeKeyOf(rec.a, rec.b))
    if (ov) {
      // the face the override speaks for first (trim reads the first face's slope)
      const own = rec.faces.filter((k) => faces[k].edgeIndex === ov.edgeIndex)
      const ordered = [...own, ...rec.faces.filter((k) => !own.includes(k))]
      const f = faces[ordered[0]]
      edges.push({ kind: ov.kind, a: [rec.a[0], rec.a[1], f.zAt(rec.a)], b: [rec.b[0], rec.b[1], f.zAt(rec.b)], faces: ordered, joinRun: true })
      continue
    }
    const f0 = faces[rec.faces[0]]
    const za = f0.zAt(rec.a)
    const zb = f0.zAt(rec.b)
    let kind: RoofEdgeKind
    if (rec.faces.length >= 2) {
      kind = classifyShared(faces, rec.occ, rec.a, rec.b, za, zb)
    } else {
      // TEAR guard (2026-08-12): an apparent-perimeter edge can really be one
      // side of a torn boundary — the two sides split at DIFFERENT event
      // points, so exact keys never pair them. Scan geometrically: a collinear
      // overlap with another face at different z is a 'step', never an
      // eave/rake (trim and framing would dress it otherwise — the tear lies
      // on a gable line, so the barge toggle would match it).
      //
      // MISSED-SHARED-EDGE guard (2026-08-20, Matt's roof-plan report: "a
      // ridge is also called an eave in here"): the SAME geometric scan can
      // also find a partner at the SAME z — a genuinely shared ridge/hip/
      // valley line whose exact-key census pairing missed by a coordinate
      // straddling the keyPt rounding boundary (measured live: two
      // independently-computed endpoints 1.1e-8 ft apart — sub-thousandth-
      // of-an-inch noise — landed on opposite sides of the 1e-5 ft grid's
      // half-integer cusp, producing DIFFERENT rounded keys for the same
      // physical point). Before this fix such an edge fell through to the
      // plain "level → eave, sloped → rake" perimeter default regardless of
      // it being a real interior joint. Now: the largest non-torn same-z
      // overlap found is treated as a genuine census partner and run through
      // the SAME classifyShared() every exactly-paired edge uses — ridge/hip/
      // valley discrimination, not a perimeter guess.
      const fi = rec.faces[0]
      let torn = false
      let bestPartner: { gi: number; fwd: boolean; ovLen: number } | null = null
      for (let gi = 0; gi < faces.length && !torn; gi++) {
        if (gi === fi) continue
        const g = faces[gi]
        for (let j = 0; j < g.ring.length; j++) {
          const c = g.ring[j], d = g.ring[(j + 1) % g.ring.length]
          const ov = collinearOverlap(rec.a, rec.b, c, d)
          if (!ov) continue
          const mid: Pt2 = [(ov[0][0] + ov[1][0]) / 2, (ov[0][1] + ov[1][1]) / 2]
          const ovLen = Math.hypot(ov[1][0] - ov[0][0], ov[1][1] - ov[0][1])
          if (Math.abs(f0.zAt(mid) - g.zAt(mid)) > 1e-6 + ovLen * 2e-4) { torn = true; break }
          if (!bestPartner || ovLen > bestPartner.ovLen) {
            // fwd mirrors the exact-match census's own convention: true when
            // this partner's OWN edge starts where rec.a is (same direction
            // as f's a→b), false when it starts at rec.b (the normal case for
            // two adjacent CCW-wound faces sharing an edge in opposite senses)
            const distCa = Math.hypot(c[0] - rec.a[0], c[1] - rec.a[1])
            const distCb = Math.hypot(c[0] - rec.b[0], c[1] - rec.b[1])
            bestPartner = { gi, fwd: distCa < distCb, ovLen }
          }
        }
      }
      if (torn) {
        kind = 'step'
      } else if (bestPartner && bestPartner.ovLen > Math.hypot(rec.b[0] - rec.a[0], rec.b[1] - rec.a[1]) * 0.9) {
        const occ = [{ fi, fwd: true }, { fi: bestPartner.gi, fwd: bestPartner.fwd }]
        kind = classifyShared(faces, occ, rec.a, rec.b, za, zb)
      } else {
        // perimeter: level → eave (a shed's high side reads as its high-side eave),
        // sloped → rake (gable ends)
        const level = Math.abs(za - zb) <= LEVEL_TOL_OF(Math.hypot(rec.b[0] - rec.a[0], rec.b[1] - rec.a[1]))
        kind = level ? 'eave' : 'rake'
      }
    }
    edges.push({
      kind,
      a: [rec.a[0], rec.a[1], za],
      b: [rec.b[0], rec.b[1], zb],
      faces: rec.faces.slice(),
    })
  }

  const skFaces: RoofSkeletonFace[] = faces.map((f) => ({
    edgeIndex: f.edgeIndex,
    ...(f.tucked ? { tucked: true } : {}),
    ring: f.ring.map((p) => [p[0], p[1]] as Pt2),
    slopeRise: f.m * 12,
    downhill: f.m > 0 ? [-f.uphill[0], -f.uphill[1]] : [0, 0],
    plane: {
      origin: [f.origin[0], f.origin[1]],
      // PER-EDGE BEARING HEIGHT: the face's own bearing height (= zPlateFt unless offset)
      zOriginFt: f.zAt(f.origin),
      risePerRun: f.m,
      uphill: [f.uphill[0], f.uphill[1]],
    },
  }))

  const n = solved.ring.length
  const plateLines: RoofPlateLine[] = solved.flat
    ? solved.ring.map((p, i) => ({
        edgeIndex: i,
        a: [p[0], p[1], zPlateFt] as Pt3,
        b: [solved.ring[(i + 1) % n][0], solved.ring[(i + 1) % n][1], zPlateFt] as Pt3,
      }))
    : faces.map((f) => {
        const a = solved.ring[f.edgeIndex % n]
        const b = solved.ring[(f.edgeIndex + 1) % n]
        // PER-EDGE BEARING HEIGHT: each plate line at its own face's bearing height
        const z = f.zAt([a[0], a[1]])
        return { edgeIndex: f.edgeIndex, a: [a[0], a[1], z] as Pt3, b: [b[0], b[1], z] as Pt3 }
      })

  return { eaveRing, faces: skFaces, edges, plateLines, zPlateFt }
}
